import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Plus, ChevronLeft, ChevronRight, Package, ShoppingBag } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../services/api';
import { toast } from 'sonner';

const HindbagImport = ({ onImported }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [importingRef, setImportingRef] = useState(null);

  const fetchProducts = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await api.get(`/provider/import/hindbag/browse?page=${p}`);
      setProducts(res.data.products || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement Hindbag');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(page); }, [page, fetchProducts]);

  const handleImport = async (product) => {
    setImportingRef(product.ref);
    try {
      await api.post('/provider/import/hindbag/add-single', { product });
      toast.success(`${product.name} ajoute au catalogue !`);
      onImported();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur import'); }
    setImportingRef(null);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="hindbag-import">
      <div className="flex items-center gap-3 mb-4">
        <ShoppingBag className="w-6 h-6 text-rose-500" />
        <h3 className="font-heading font-bold text-base uppercase">Import Hindbag</h3>
        <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 font-bold rounded">{total} produits</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-rose-500" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4">
            <AnimatePresence>
              {products.map(p => (
                <motion.div key={p.ref} className="bg-white border overflow-hidden group hover:shadow-md transition-shadow" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid={`hindbag-product-${p.ref}`}>
                  <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden relative">
                    {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" /> : <Package className="w-16 h-16 text-slate-200" />}
                    {p.images?.length > 1 && <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">{p.images.length} photos</span>}
                    {p.stock_status !== 'instock' && <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] px-2 py-0.5 font-bold rounded">Rupture</span>}
                  </div>
                  <div className="p-3">
                    <p className="font-heading font-bold text-xs leading-tight line-clamp-2 mb-1">{p.name}</p>
                    {p.description && <p className="text-[10px] text-slate-500 line-clamp-2 mb-2">{p.description}</p>}
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-heading font-bold text-sm text-rose-600">{p.price > 0 ? `${p.price}€` : 'Sur devis'}</span>
                    </div>
                    <Button size="sm" className="w-full mt-2 bg-rose-500 text-white text-xs gap-1" disabled={importingRef === p.ref || p.stock_status !== 'instock'} onClick={() => handleImport(p)} data-testid={`import-hindbag-${p.ref}`}>
                      {importingRef === p.ref ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      Ajouter au catalogue
                    </Button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-center gap-3 pb-4">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="hindbag-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-xs font-heading">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="hindbag-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default HindbagImport;
